import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';

const GRNPrint = ({ grn, purchaseEntry, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);

  useEffect(() => {
    if (autoPrint && grn && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [autoPrint, grn]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy hh:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const handlePrint = () => {
    if (!grn) return;

    // When grn is a Purchase Entry (from completedEntries), items have supplier_qty, supplier_unit, supplier_rate
    // When grn is a Store Receipt, items have quantity_received, item_unit, po_price
    const items = grn.items || [];
    const isPurchaseEntry = items.length > 0 && items[0].supplier_qty !== undefined;
    
    // Build items table rows - handle both Purchase Entry and Store Receipt item structures
    const itemRows = items.map((item, idx) => {
      // Supplier quantities (from invoice)
      const supplierQty = isPurchaseEntry ? (item.supplier_qty || 0) : (item.quantity_received || 0);
      const supplierUnit = isPurchaseEntry ? (item.supplier_unit || '-') : (item.item_unit || item.unit || '-');
      const supplierRate = isPurchaseEntry ? (item.supplier_rate || 0) : (item.po_price || 0);
      const amount = supplierQty * supplierRate;
      
      // System quantities (converted to system UOM)
      const systemQty = isPurchaseEntry ? (item.system_qty || supplierQty) : (item.quantity_received || 0);
      const systemUnit = isPurchaseEntry ? (item.system_unit || supplierUnit) : (item.item_unit || item.unit || '-');
      
      return `
      <tr>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${idx + 1}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">
          <div style="font-weight: 600;">${item.item_name}</div>
          ${item.indent_number ? `<div style="font-size: 10px; color: #666;">Indent: ${item.indent_number}</div>` : ''}
        </td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${item.po_number || '-'}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${systemQty}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${systemUnit}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${supplierQty}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${supplierUnit}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${supplierRate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>
    `;
    }).join('');

    // Calculate totals based on data source
    const totalSupplierQty = items.reduce((sum, item) => {
      const qty = isPurchaseEntry ? (item.supplier_qty || 0) : (item.quantity_received || 0);
      return sum + qty;
    }, 0);
    const totalSystemQty = items.reduce((sum, item) => {
      const qty = isPurchaseEntry ? (item.system_qty || item.supplier_qty || 0) : (item.quantity_received || 0);
      return sum + qty;
    }, 0);
    const totalAmount = items.reduce((sum, item) => {
      const qty = isPurchaseEntry ? (item.supplier_qty || 0) : (item.quantity_received || 0);
      const rate = isPurchaseEntry ? (item.supplier_rate || 0) : (item.po_price || 0);
      return sum + (qty * rate);
    }, 0);

    // Get timestamps
    const gateEntryTime = grn.gate_entry_timestamp || grn.created_at;
    const storeEntryTime = grn.store_receipt_timestamp || grn.receipt_date || grn.created_at;
    const purchaseEntryTime = purchaseEntry?.created_at || grn.updated_at;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>GRN - ${grn.store_receipt_no}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 12px; 
            padding: 20px;
            max-width: 210mm;
            margin: 0 auto;
          }
          .header { 
            text-align: center;
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header h1 { 
            font-size: 18px; 
            margin-bottom: 5px;
            color: #333;
          }
          .header h2 {
            font-size: 14px;
            color: #666;
            font-weight: normal;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .info-box {
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fafafa;
          }
          .info-box h3 {
            font-size: 11px;
            color: #666;
            margin-bottom: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #eee;
            text-transform: uppercase;
          }
          .info-box p {
            margin: 4px 0;
            font-size: 11px;
          }
          .info-box .value {
            font-weight: 600;
            color: #333;
          }
          .info-box .label {
            color: #666;
            font-size: 10px;
          }
          .timeline {
            margin-bottom: 20px;
            padding: 15px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
          }
          .timeline h3 {
            font-size: 12px;
            margin-bottom: 12px;
            color: #334155;
          }
          .timeline-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 10px;
          }
          .timeline-item {
            padding: 10px;
            border-radius: 4px;
            text-align: center;
          }
          .timeline-item.gate { background: #dbeafe; border: 1px solid #93c5fd; }
          .timeline-item.store { background: #dcfce7; border: 1px solid #86efac; }
          .timeline-item.purchase { background: #fef3c7; border: 1px solid #fcd34d; }
          .timeline-item .step {
            font-size: 10px;
            font-weight: bold;
            margin-bottom: 4px;
          }
          .timeline-item.gate .step { color: #1e40af; }
          .timeline-item.store .step { color: #166534; }
          .timeline-item.purchase .step { color: #92400e; }
          .timeline-item .time {
            font-size: 11px;
            font-weight: 600;
            color: #333;
          }
          .timeline-item .date {
            font-size: 10px;
            color: #666;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          th { 
            background: #f1f5f9; 
            padding: 8px 6px; 
            border: 1px solid #ddd; 
            text-align: left;
            font-weight: bold;
            color: #334155;
            font-size: 11px;
          }
          td { 
            padding: 6px; 
            border: 1px solid #ddd;
            vertical-align: top;
            font-size: 11px;
          }
          .totals-row {
            background: #f8fafc;
            font-weight: bold;
          }
          .footer {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 20px;
          }
          .signature-box {
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 40px;
            padding-top: 5px;
            font-size: 11px;
          }
          .notes {
            margin-top: 15px;
            padding: 10px;
            background: #fffbeb;
            border: 1px solid #fcd34d;
            border-radius: 4px;
            font-size: 11px;
          }
          .notes h4 {
            margin-bottom: 5px;
            color: #92400e;
          }
          @media print {
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>GOODS RECEIPT NOTE (GRN)</h1>
          <h2>${grn.store_receipt_no}</h2>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <h3>GRN Details</h3>
            <p><span class="label">GRN No:</span> <span class="value">${grn.store_receipt_no}</span></p>
            <p><span class="label">Receipt Date:</span> <span class="value">${formatDate(grn.receipt_date)}</span></p>
            <p><span class="label">Nature:</span> <span class="value">${grn.nature_of_inward || '-'}</span></p>
          </div>
          <div class="info-box">
            <h3>Supplier Details</h3>
            <p><span class="label">Supplier:</span> <span class="value">${grn.supplier_name}</span></p>
            <p><span class="label">Invoice/DC No:</span> <span class="value">${grn.invoice_dc_no || '-'}</span></p>
            <p><span class="label">PO Number:</span> <span class="value">${grn.po_number || items[0]?.po_number || '-'}</span></p>
          </div>
          <div class="info-box">
            <h3>Reference</h3>
            <p><span class="label">Gate Entry:</span> <span class="value">${grn.gate_entry_no || '-'}</span></p>
            <p><span class="label">Unit:</span> <span class="value">${grn.unit_code || '-'}</span></p>
            <p><span class="label">Routing:</span> <span class="value">${grn.routed_to || '-'}</span></p>
          </div>
        </div>

        <div class="timeline">
          <h3>📋 Process Timeline</h3>
          <div class="timeline-grid">
            <div class="timeline-item gate">
              <div class="step">1. GATE ENTRY</div>
              <div class="time">${formatDateTime(gateEntryTime)}</div>
            </div>
            <div class="timeline-item store">
              <div class="step">2. STORE ENTRY</div>
              <div class="time">${formatDateTime(storeEntryTime)}</div>
            </div>
            <div class="timeline-item purchase">
              <div class="step">3. PURCHASE ENTRY</div>
              <div class="time">${purchaseEntryTime ? formatDateTime(purchaseEntryTime) : 'Pending'}</div>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">S.No</th>
              <th style="min-width: 130px;">Item</th>
              <th style="width: 75px; text-align: center;">PO No.</th>
              <th style="width: 60px; text-align: right;">Sys Qty</th>
              <th style="width: 50px; text-align: center;">UOM</th>
              <th style="width: 60px; text-align: right;">Supp Qty</th>
              <th style="width: 50px; text-align: center;">UOM</th>
              <th style="width: 65px; text-align: right;">Rate</th>
              <th style="width: 80px; text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
            <tr class="totals-row">
              <td colspan="3" style="text-align: right; padding: 8px;">Total:</td>
              <td style="text-align: right; padding: 8px;">${totalSystemQty}</td>
              <td></td>
              <td style="text-align: right; padding: 8px;">${totalSupplierQty}</td>
              <td></td>
              <td></td>
              <td style="text-align: right; padding: 8px;">₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>

        ${purchaseEntry ? `
        <div class="info-box" style="margin-bottom: 20px;">
          <h3>Purchase Entry Details</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px;">
            <p><span class="label">Sub Total:</span> <span class="value">₹${(purchaseEntry.total_supplier_amount || purchaseEntry.sub_total || totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Other Charges (Taxable):</span> <span class="value">₹${(purchaseEntry.other_charges_taxable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">GST (${purchaseEntry.gst_rate || 0}%):</span> <span class="value">₹${(purchaseEntry.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Other Charges (Non-Taxable):</span> <span class="value">₹${(purchaseEntry.other_charges_non_taxable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
          </div>
          <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd; text-align: right;">
            <p style="font-size: 14px;"><span class="label">Grand Total:</span> <span class="value" style="font-size: 16px; font-weight: bold; color: #166534;">₹${(purchaseEntry.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
          </div>
        </div>
        ` : ''}

        ${purchaseEntry?.payment_invoice_amount ? `
        <div class="info-box" style="margin-bottom: 20px; border-color: #86efac; background: #f0fdf4;">
          <h3 style="color: #166534;">Payment Information</h3>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px;">
            <p><span class="label">Invoice Amount:</span> <span class="value">₹${(purchaseEntry.payment_invoice_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Advance Paid:</span> <span class="value">₹${(purchaseEntry.payment_advance_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Balance:</span> <span class="value" style="color: ${(purchaseEntry.payment_balance || 0) > 0 ? '#dc2626' : '#166534'}; font-weight: bold;">₹${(purchaseEntry.payment_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
            <p><span class="label">Due Date:</span> <span class="value">${purchaseEntry.payment_due_date ? formatDate(purchaseEntry.payment_due_date) : '-'}</span></p>
          </div>
        </div>
        ` : ''}

        ${grn.quality_notes || purchaseEntry?.notes ? `
        <div class="notes">
          <h4>Notes / Remarks:</h4>
          <p>${grn.quality_notes || ''} ${purchaseEntry?.notes || ''}</p>
        </div>
        ` : ''}

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line">Security / Gate</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Store Incharge</div>
          </div>
          <div class="signature-box">
            <div class="signature-line">Purchase / Accounts</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        if (onPrintComplete) {
          onPrintComplete();
        }
      }, 250);
    }
  };

  return null;
};

export default GRNPrint;
